/**
 * GSMS Handling Type Utility Functions
 * 
 * Maps between full names and short codes for GSMS Handling Types
 * Mapping: 
 * - "Private Label - CII" -> "PL2"
 * - "Marketing Income" -> "MI"
 * - "Repackage" -> "R"
 * - "Overlabel" -> "OL"
 * - "Private Label" -> "PL"
 * - Default -> " " (empty space)
 */

export interface GSMSHandlingTypeMapping {
  fullName: string;
  shortCode: string;
}

/**
 * Mapping of full names to short codes
 */
export const GSMS_HANDLING_TYPE_MAP: Record<string, string> = {
  'Private Label - CII': 'PL2',
  'Private Label CII': 'PL2', // Handle API format without dash
  'Marketing Income': 'MI',
  'Repackage': 'R',
  'Repackage - R': 'R', // Handle format with code suffix
  'Overlabel': 'OL',
  'Overlabel - OL': 'OL', // Handle format with code suffix
  'Private Label': 'PL',
  'Private Label - PL': 'PL', // Handle format with code suffix
};

/**
 * Reverse mapping of short codes to full names
 */
export const GSMS_HANDLING_TYPE_REVERSE_MAP: Record<string, string> = {
  'PL2': 'Private Label - CII',
  'MI': 'Marketing Income',
  'R': 'Repackage',
  'OL': 'Overlabel',
  'PL': 'Private Label',
};

/**
 * Get short code from full name
 * Handles formats like:
 * - "Repackage" -> "R"
 * - "Repackage - R" -> "R" (extracts code after " - ")
 * - "R" -> "R" (already a short code)
 * @param fullName - Full name of GSMS Handling Type (may include " - CODE" format)
 * @returns Short code or empty space if not found
 */
export const getShortCodeFromFullName = (fullName: string | null | undefined): string => {
  if (!fullName) return ' ';
  const trimmed = fullName.trim();
  if (!trimmed) return ' ';
  
  // Check if format is "Full Name - CODE" (e.g., "Repackage - R")
  // Extract the code after " - "
  if (trimmed.includes(' - ')) {
    const parts = trimmed.split(' - ');
    if (parts.length >= 2) {
      const code = parts[parts.length - 1].trim();
      // If the extracted code is a valid short code, return it
      if (code && GSMS_HANDLING_TYPE_REVERSE_MAP[code]) {
        return code;
      }
    }
  }
  
  // Check if it's already a short code
  if (GSMS_HANDLING_TYPE_REVERSE_MAP[trimmed]) {
    return trimmed;
  }
  
  // Try to find in the map by full name
  const mappedCode = GSMS_HANDLING_TYPE_MAP[trimmed];
  return mappedCode || ' ';
};

/**
 * Get full name from short code
 * @param shortCode - Short code of GSMS Handling Type
 * @returns Full name or empty string if not found
 */
export const getFullNameFromShortCode = (shortCode: string | null | undefined): string => {
  if (!shortCode) return '';
  const trimmed = shortCode.trim();
  return GSMS_HANDLING_TYPE_REVERSE_MAP[trimmed] || '';
};

/**
 * Format handling type for display (e.g., "Marketing Income - MI")
 * @param fullName - Full name of GSMS Handling Type
 * @returns Formatted string like "Marketing Income - MI" or just the full name if no mapping exists
 */
export const formatHandlingTypeForDisplay = (fullName: string): string => {
  if (!fullName) return '';
  
  const shortCode = getShortCodeFromFullName(fullName);
  if (shortCode === ' ') return fullName; // No mapping found, return as-is
  
  // Normalize the full name - if it's "Private Label CII" convert to "Private Label - CII"
  let normalizedName = fullName;
  if (fullName === 'Private Label CII') {
    normalizedName = 'Private Label - CII';
  }
  
  // If the name already contains the code, don't add it again
  if (normalizedName.includes(` - ${shortCode}`)) {
    return normalizedName;
  }
  
  return `${normalizedName} - ${shortCode}`;
};

/**
 * Create dropdown options with formatted display labels
 * @param handlingTypesList - List of handling types from API
 * @returns Array of options with formatted labels
 */
export const createHandlingTypeOptions = (
  handlingTypesList: Array<{ id: number; handling_type: string }>
): Array<{ label: string; value: string }> => {
  return handlingTypesList.map(ht => ({
    label: formatHandlingTypeForDisplay(ht.handling_type),
    value: ht.handling_type, // Store full name as value
  }));
};

